import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { clearFcmTokenOnBackend } from "../services/firebase.service";
import { showToast } from "../redux/guest-slices/other.slice";

const TIMEOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
  "touchstart",
];
const STORAGE_KEY = "specialist_last_activity";
const THROTTLE_DELAY = 5000; // 5 seconds

export const useSessionTimeout = (timeoutMs = TIMEOUT_DURATION) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    const logoutUser = () => {
      clearFcmTokenOnBackend().catch((e) => {
        console.error("Failed to clear FCM token on logout:", e);
      });
      dispatch({ type: "Logout", payload: {} });
      dispatch(
        showToast({
          type: "info",
          message: "Session timed out due to inactivity. Please log in again.",
        })
      );
    };

    const checkSession = () => {
      const lastActivity = Number(localStorage.getItem(STORAGE_KEY) || 0);
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      if (timeSinceLastActivity >= timeoutMs) {
        logoutUser();
      } else {
        const remainingTime = timeoutMs - timeSinceLastActivity;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(checkSession, remainingTime);
      }
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdateRef.current > THROTTLE_DELAY) {
        localStorage.setItem(STORAGE_KEY, now.toString());
        lastUpdateRef.current = now;
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(checkSession, timeoutMs);
    };

    if (!localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        const lastActivity = Number(e.newValue || 0);
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivity;
        if (timeSinceLastActivity < timeoutMs) {
          const remainingTime = timeoutMs - timeSinceLastActivity;
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(checkSession, remainingTime);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);

    timerRef.current = setTimeout(checkSession, timeoutMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch, timeoutMs]);
};

export default useSessionTimeout;
